/**
 * Checkbox.tsx
 *
 * ── Touch floor below `md` ──────────────────────────────────────────────────────
 * The control stays 16px; an invisible 44×44 label sits over it, absolutely positioned
 * so it costs no layout. That matters: 44 files use this, several inside dense table
 * rows, and growing the box or padding its wrapper would shift every one of them.
 *
 * Two mechanisms were not available. A pseudo-element on the input is out because
 * replaced elements like `input` do not generate `::before`/`::after` at all — and the
 * wrapper had no positioned ancestor to hang one on either. Padding on the wrapper does
 * nothing, because padding on a `div` around an input is not part of the input's hit
 * area. A `label` is, which is why that is what the overlay is.
 *
 * The overlay needs an id to point at, so one is generated when the caller supplies
 * neither an `id` nor a string `label`. A side effect worth knowing: the visible text
 * label previously had `htmlFor={undefined}` in that case, so clicking it did not toggle
 * anything. It does now.
 *
 * ── Locked (FieldLockScope) ─────────────────────────────────────────────────────
 * Inside a locked scope the box shows its state and takes no change: its
 * onChange is dropped, so React puts a controlled box straight back after a
 * click (aria-readonly says so to assistive tech). It is never natively
 * `disabled` there, because a browser draws a disabled native checkbox greyed.
 */
import React, { useId } from 'react';
import { cn } from '@/lib/utils';
import { useFieldLocked } from './FieldLock';

/** What a locked checkbox gets on top of its props: read-only, no change, not greyed. */
const LOCKED_CHECKBOX_PROPS = {
  disabled: undefined,
  readOnly: true,
  'aria-readonly': true,
  onChange: undefined,
} as const;

export interface CheckboxProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: React.ReactNode;
  description?: string;
  error?: string;
  wrapperClassName?: string;
}

export const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  ({ label, description, error, wrapperClassName, className, id, ...props }, ref) => {
    const locked = useFieldLocked();
    const generatedId = useId();
    const inputId =
      id ??
      (typeof label === 'string' ? label.toLowerCase().replace(/\s+/g, '-') : generatedId);
    const errorId = error ? `${inputId}-error` : undefined;

    return (
      <div className={cn('flex items-start gap-3', wrapperClassName)}>
        <div className="relative flex h-5 items-center">
          <input
            ref={ref}
            id={inputId}
            type="checkbox"
            aria-invalid={!!error}
            aria-describedby={errorId}
            className={cn(
              'h-4 w-4 rounded border-slate-300 text-blue-600',
              'focus:ring-2 focus:ring-blue-500 focus:ring-offset-1',
              locked ? 'cursor-default' : 'disabled:cursor-not-allowed disabled:opacity-50',
              !locked && 'cursor-pointer',
              error && 'border-red-500',
              className,
            )}
            {...props}
            {...(locked ? LOCKED_CHECKBOX_PROPS : {})}
          />

          {/*
            The touch target. Absolutely positioned and centred on the control, so it
            adds 44×44 of tappable area without occupying any. Empty, so it contributes
            nothing to the accessible name — `aria-label` or the visible label below
            still supplies that.
          */}
          <label
            htmlFor={inputId}
            data-testid="checkbox-touch-target"
            className="absolute left-1/2 top-1/2 hidden h-11 w-11 -translate-x-1/2 -translate-y-1/2 cursor-pointer max-md:block"
          />
        </div>

        {(label || description) && (
          <div>
            {label && (
              <label
                htmlFor={inputId}
                className={`text-sm font-medium text-slate-700 ${locked ? 'cursor-default' : 'cursor-pointer'} select-none`}
              >
                {label}
              </label>
            )}
            {description && <p className="text-xs text-slate-500 mt-0.5">{description}</p>}
            {error && (
              <p id={errorId} role="alert" className="text-xs text-red-600 mt-0.5">
                {error}
              </p>
            )}
          </div>
        )}
      </div>
    );
  },
);

Checkbox.displayName = 'Checkbox';
